import { toast } from 'sonner';
import i18n from 'i18n';
import { finishReceipt } from 'services/api/receipt';
import { useAuthStore } from 'store/authStore';
import { useReceiptStore } from 'store/receiptStore';
import { useTrackingStore } from 'store/trackingStore';
import { useAppStore } from 'store/appStore';
import { useProductStore } from 'store/productStore';
import { ReceiptData } from 'types';
import { SCREEN_TYPES } from 'utils/constants';
import { resetAllProductRelatedStores } from 'utils/index';
import { playErrorSound, playFanfareSound, playGoodSound } from './sounds';

interface CloseReceiptOptions {
  // Closed automatically after the last item was graded
  auto?: boolean;
}

// A receipt auto-closes only when it isn't on hold for a problem and every line has been graded
export const isReceiptReadyToAutoClose = (receiptData: ReceiptData | null): boolean => {
  const problemStatus = receiptData?.receipt?.problem_status;
  const isOnHold = !!problemStatus && problemStatus !== 'none';
  const lines = receiptData?.lines?.filter(Boolean) ?? [];
  return !isOnHold && lines.length > 0 && lines.every((line) => line.receipt_ind === 'y');
};

export const closeCurrentReceipt = async ({
  auto = false,
}: CloseReceiptOptions = {}): Promise<boolean> => {
  const { user, stationId } = useAuthStore.getState();
  const { receiptData, setReceiptData, setClosedTrackingId } = useReceiptStore.getState();
  const { pendingTrackingId, setPendingTrackingId, setLastTrackingId, setCurrentTrackingId } =
    useTrackingStore.getState();
  const { setCurrentScreen } = useAppStore.getState();
  const { currentProduct, setCurrentProduct, setShowGradingSheet } = useProductStore.getState();

  const transactionId = receiptData?.transaction_id || receiptData?.receipt?.transaction_id;
  const trackingId = receiptData?.tracking_id || receiptData?.receipt?.tracking_id || '';

  try {
    const result = await finishReceipt(transactionId, user?.id ?? '', stationId ?? '');

    if (result?.responseState === 'confirmed') {
      setReceiptData(null);
      setCurrentProduct(null);
      setLastTrackingId(null);
      setPendingTrackingId(null);
      setCurrentTrackingId('');
      if (!pendingTrackingId) {
        resetAllProductRelatedStores();
      }

      if (auto) {
        setCurrentProduct(currentProduct);
        setShowGradingSheet(true);
        setClosedTrackingId(trackingId);
        playFanfareSound();
      } else {
        toast.success(i18n.t('toast.receiptClosedSuccess'));
        playGoodSound();
      }
      setCurrentScreen(SCREEN_TYPES.SCAN_BOX);
      return true;
    } else {
      playErrorSound();
      toast.error(result?.responseMsg || i18n.t('toast.failedCloseReceipt'));
      return false;
    }
  } catch {
    toast.error(i18n.t('toast.networkError'));
    return false;
  }
};
